"use client";
import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useContactModal } from '../../../../contexts/ContactModalContext';

const includes = [
    'UX Audit',
    'User Journey Analysis',
    'Usability Review',
    'Competitor Experience Analysis',
    'Product Experience Roadmap',
];

export default function UXFinalCTA() {
    const { openModal } = useContactModal();

    return (
        <section
            className="relative overflow-hidden py-24 px-6 md:px-16 lg:px-24"
            style={{ background: '#0a0a0a', borderTopLeftRadius: '80px', borderTopRightRadius: '80px' }}
        >
            <div className="absolute inset-0 pointer-events-none opacity-[0.025]" style={{
                backgroundImage: 'linear-gradient(rgba(206,255,26,1) 1px, transparent 1px), linear-gradient(90deg, rgba(206,255,26,1) 1px, transparent 1px)',
                backgroundSize: '60px 60px',
            }} />

            <div className="max-w-[900px] mx-auto relative z-10 text-center flex flex-col gap-10 items-center">

                <motion.span
                    initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    className="text-xs font-bold tracking-[0.2em] uppercase"
                    style={{ color: '#ceff1a', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                    Get In Touch
                </motion.span>

                <motion.h2
                    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
                    className="text-2xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-wide"
                    style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#ffffff' }}>
                    Ready To Create Better User Experiences?
                </motion.h2>

                <motion.p
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}
                    className="text-base md:text-lg max-w-2xl"
                    style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(251,253,253,0.65)', lineHeight: '1.8', letterSpacing: '0.02em' }}>
                    Whether you&apos;re building a product, redesigning an application, or improving conversion rates, we&apos;ll help you create experiences rooted in real user behaviour.
                </motion.p>

                {/* Checklist */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                    className="flex flex-col gap-3 w-full max-w-sm text-left"
                >
                    <p className="text-xs font-bold tracking-[0.2em] uppercase text-center mb-2"
                        style={{ color: 'rgba(251,253,253,0.4)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                        Your UX Strategy Session Includes
                    </p>
                    {includes.map((item, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                            transition={{ delay: 0.25 + i * 0.07 }}
                            className="flex items-center gap-3"
                        >
                            <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                                style={{ background: 'rgba(206,255,26,0.15)', border: '1px solid rgba(206,255,26,0.3)' }}>
                                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                    <path d="M2 5l2 2 4-4" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <span className="text-sm font-medium"
                                style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(251,253,253,0.75)' }}>
                                {item}
                            </span>
                        </motion.div>
                    ))}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}
                    className="flex flex-col sm:flex-row gap-4 items-center w-full sm:w-auto">
                    <button onClick={openModal}
                        className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-4 rounded-full font-semibold text-base md:text-lg transition-all duration-300 hover:scale-105"
                        style={{ background: '#ceff1a', color: '#080400', fontFamily: 'var(--font-clash), "Clash Display", sans-serif', letterSpacing: '0.05em' }}>
                        Book A UX Strategy Session →
                    </button>
                    <Link href="/contact"
                        className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-4 rounded-full font-semibold text-base md:text-lg transition-all duration-300 hover:scale-105"
                        style={{ border: '1.5px solid rgba(206,255,26,0.35)', color: '#fbfdfd', fontFamily: 'var(--font-clash), "Clash Display", sans-serif', letterSpacing: '0.05em' }}>
                        Talk to a UX Strategist
                    </Link>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 }}
                    className="text-xs"
                    style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(251,253,253,0.3)', letterSpacing: '0.05em' }}>
                    Lead Workz — Build for visibility. Optimised for usability. Designed for growth.
                </motion.p>

            </div>
        </section>
    );
}
